import { useCallback, useEffect, useMemo } from "react";

import { type ApolloError } from "@apollo/client";
import { isEqual } from "es-toolkit";
import { useAtom } from "jotai";

import { useBlizzardEvents } from "../../analytics/hooks";
import { type MyLensesLensCta, type Visibility } from "../../gql-generated/types";
import { mapPreviewMediaItemsToResources } from "../../shared/state";
import { type LensPreviewMediaItem } from "../../shared/state/LensPreviewMediaState";
import { type ChangedField, type LensEditableState, lensEditedStateAtom, lensStateAtom } from "../state/lensImmerState";
import {
    validateAndSanitizeDescription,
    validateAndSanitizeSpectaclesTags,
    validateAndSanitizeTags,
    validateLensId,
} from "../validation/lensValidation";

import { useUpdateLens } from "./useUpdateLens";

const getEditableStateKeys = (state: Partial<LensEditableState>): (keyof LensEditableState)[] => {
    return Object.keys(state) as (keyof LensEditableState)[];
};

export interface LensChangeTracker {
    creatorRewardProgramEnrolled: boolean | undefined;
    updateCreatorPayout: (enrolled: boolean) => void;

    description: string | null | undefined;
    updateDescription: (description: string) => void;

    lensCta: MyLensesLensCta | null | undefined;
    updateCta: (cta: MyLensesLensCta | null) => void;

    previewMediaItems: LensPreviewMediaItem[] | undefined;
    updatePreviewMedia: (media: LensPreviewMediaItem[]) => void;

    spectaclesTags: string[] | null | undefined;
    updateSpectaclesTags: (tags: string[]) => void;

    tags: string[] | null | undefined;
    updateTags: (tags: string[]) => void;

    profileVisibility: Visibility | null | undefined;
    updateVisibility: (visibility: Visibility | null) => void;

    hasChanges: boolean;
    cancelChanges: () => void;
    getChangedFields: () => ChangedField[];
    hasFieldChanged: (field: keyof LensEditableState) => boolean;

    saveLens: () => Promise<void>;
    updateLensLoading: boolean;
    updateLensError: ApolloError | undefined;
}

export const useLensChangeTracker = (lensId: string): LensChangeTracker => {
    const [editedState, setEditedState] = useAtom(lensEditedStateAtom(lensId));
    const [serverState] = useAtom(lensStateAtom(lensId));
    const { updateLens, loading: updateLensLoading, error: updateLensError } = useUpdateLens();
    const { sendBlizzardEvent } = useBlizzardEvents();

    useEffect(() => {
        return () => {
            setEditedState({
                creatorRewardProgramEnrolled: false,
                description: "",
                isGameUserProvided: false,
                lensCta: null,
                previewMediaItems: [],
                spectaclesTags: [],
                tags: [],
                profileVisibility: null,
            });
        };
    }, [lensId, setEditedState]);

    const updateField = useCallback(
        (updater: (draft: Partial<LensEditableState>) => void) => {
            setEditedState(updater);
        },
        [setEditedState],
    );

    const updateCreatorPayout = useCallback(
        (enrolled: boolean) => {
            updateField((draft) => {
                draft.creatorRewardProgramEnrolled = enrolled;
            });
        },
        [updateField],
    );

    const updateDescription = useCallback(
        (description: string) => {
            updateField((draft) => {
                draft.description = validateAndSanitizeDescription(description);
            });
        },
        [updateField],
    );

    const updateCta = useCallback(
        (cta: MyLensesLensCta | null) => {
            updateField((draft) => {
                draft.lensCta = cta;
            });
        },
        [updateField],
    );

    const updatePreviewMedia = useCallback(
        (media: LensPreviewMediaItem[]) => {
            updateField((draft) => {
                draft.previewMediaItems = media;
            });
        },
        [updateField],
    );

    const updateSpectaclesTags = useCallback(
        (tags: string[]) => {
            updateField((draft) => {
                draft.spectaclesTags = validateAndSanitizeSpectaclesTags(tags);
            });
        },
        [updateField],
    );

    const updateTags = useCallback(
        (tags: string[]) => {
            updateField((draft) => {
                draft.tags = validateAndSanitizeTags(tags);
            });
        },
        [updateField],
    );

    const updateVisibility = useCallback(
        (visibility: Visibility | null) => {
            updateField((draft) => {
                draft.profileVisibility = visibility;
            });
        },
        [updateField],
    );

    const hasChanges = useMemo(() => {
        return getEditableStateKeys(editedState).some((field) => !isEqual(editedState[field], serverState[field]));
    }, [editedState, serverState]);

    const cancelChanges = useCallback(() => {
        setEditedState(serverState);
    }, [serverState, setEditedState]);

    const hasFieldChanged = useCallback(
        (field: keyof LensEditableState) => {
            return !isEqual(editedState[field], serverState[field]);
        },
        [editedState, serverState],
    );

    const getChangedFields = useCallback((): ChangedField[] => {
        const fieldLabels: Record<keyof LensEditableState, string> = {
            description: "Description",
            tags: "Tags",
            spectaclesTags: "Spectacles Tags",
            previewMediaItems: "Preview Media",
            profileVisibility: "Visibility",
            lensCta: "Lens CTA",
            creatorRewardProgramEnrolled: "Creator Rewards Program",
            isGameUserProvided: "Game Lens",
        };

        return getEditableStateKeys(editedState)
            .filter((field) => !isEqual(editedState[field], serverState[field]))
            .map((field) => ({
                field,
                label: fieldLabels[field] || field,
            }));
    }, [editedState, serverState]);

    const sendLensUpdateAnalytics = useCallback(
        (changedFields: Set<keyof LensEditableState>) => {
            if (changedFields.has("lensCta")) {
                sendBlizzardEvent({
                    event: "MYLENSES_MANAGEMENT_PAGE_ACTION",
                    pageType: "LENS_DETAILS",
                    action: "EDIT_CTA",
                });
            }

            if (changedFields.has("previewMediaItems")) {
                sendBlizzardEvent({
                    event: "MYLENSES_MANAGEMENT_PAGE_ACTION",
                    pageType: "LENS_DETAILS",
                    action: "CHANGE_PREVIEW",
                });
            }
        },
        [sendBlizzardEvent],
    );

    const saveLens = useCallback(async (): Promise<void> => {
        validateLensId(lensId);
        const changedFields = new Set(
            getEditableStateKeys(editedState).filter((field) => !isEqual(editedState[field], serverState[field])),
        );

        const updateVariables = {
            lensId,
            creatorRewardProgramEnrolled: editedState.creatorRewardProgramEnrolled ?? false,
            isGameUserProvided: editedState.isGameUserProvided ?? serverState.isGameUserProvided ?? false,
            ...(changedFields.has("description") && { description: editedState.description }),
            ...(changedFields.has("tags") && { tags: editedState.tags }),
            ...(changedFields.has("spectaclesTags") && { spectaclesTags: editedState.spectaclesTags }),
            ...(changedFields.has("lensCta") && { lensCta: editedState.lensCta }),
            ...(changedFields.has("profileVisibility") && { profileVisibility: editedState.profileVisibility }),
            ...(changedFields.has("previewMediaItems") && {
                previewMediaResources: editedState.previewMediaItems
                    ? mapPreviewMediaItemsToResources(editedState.previewMediaItems)
                    : undefined,
            }),
        };

        await updateLens({
            variables: updateVariables,
        });

        sendLensUpdateAnalytics(changedFields);
    }, [lensId, editedState, serverState, updateLens, sendLensUpdateAnalytics]);

    return {
        creatorRewardProgramEnrolled: editedState.creatorRewardProgramEnrolled ?? undefined,
        updateCreatorPayout,

        description: editedState.description,
        updateDescription,

        lensCta: editedState.lensCta,
        updateCta,

        previewMediaItems: editedState.previewMediaItems,
        updatePreviewMedia,

        spectaclesTags: Array.isArray(editedState.spectaclesTags) ? editedState.spectaclesTags : [],
        updateSpectaclesTags,

        tags: Array.isArray(editedState.tags) ? editedState.tags : [],
        updateTags,

        profileVisibility: editedState.profileVisibility,
        updateVisibility,

        hasChanges,
        cancelChanges,
        getChangedFields,
        hasFieldChanged,

        saveLens,
        updateLensLoading,
        updateLensError,
    };
};
